import { redirect } from 'react-router'

import { EMPTY_FUNCTION, EMPTY_OBJECT, EMPTY_STRING } from '../constants/common'
import {
  REG_EXP_INTEGER,
  REG_EXP_PHONE_PREFIX,
  REG_EXP_UUID,
} from '../constants/regExp'
import {
  LEGACY_SUBMENU_PARAM_REPLACERS,
  PROFILE_ROUTE_NAMES,
  ROUTE_PARAM_EMAIL_ACCOUNT_ID,
  ROUTE_PATH_LIST,
  ROUTE_PATH_LIST_SETTINGS,
  ROUTE_UI_SECTION_NAME,
  ROUTER_EXTERNAL_REDIRECT_PREFIX,
  SEARCH_RESULTS_QUERY_PARAM_KEY,
  SUBMENU_ALL_SECTION,
  SUBMENU_DONE_SECTION,
  SUBMENU_PARAM_ACTIVE_VALUE,
  SUBMENU_PARAM_ARCHIVE_VALUE,
  SUBMENU_PARAM_KEY,
  SUBMENU_PARAM_KEY_OLD,
  SUBMENU_PARAM_READ_VALUE,
  SUBMENU_PARAM_TRASH_VALUE,
  SUBMENU_PARAM_UNREAD_VALUE,
  URL_ALIASES,
  UTM_HOME_PARAMETER,
  WEBAPP_EMAIL_INTEGRATION_ACCOUNTS_TAB,
  WEBAPP_ROUTE_PREFIX,
} from '../constants/route'
import { EXTERNAL_CONTACT_USER_ROLE } from '../constants/user'

import { getApi } from './api'
import { getSectionCanViewPermission } from './common'
import {
  getUserId,
  isDwellerRole,
  isExternalContactUser,
  isStaffUser,
} from './user'

function getPreparedLink(link) {
  return link ? (link.startsWith('/') ? link : '/' + link) : EMPTY_STRING
}

function getUserProfileTiny(params = EMPTY_OBJECT, getPermissions = true) {
  const allParams = getPermissions ? { ...params, get_permissions: 1 } : params

  return getApi({ endpoint: 'api/profile-tiny/', params: allParams })
}

// The default profile-tiny list scope excludes external contacts, so they
// have to be requested with an explicit group filter.
async function getUserProfileTinyById({ id, withExternalContacts }) {
  try {
    const profile = extractProfile(await getUserProfileTiny({ id }))

    if (profile || !withExternalContacts) {
      return profile
    }

    return extractProfile(
      await getUserProfileTiny({ group: EXTERNAL_CONTACT_USER_ROLE, id })
    )
  } catch {
    return null
  }
}

// Maps a user to the NG profile route of their role, or null when that role's
// NG profile is not rolled out (callers fall back to the webapp profile).
function getUserProfileNGPathname({
  id,
  isExternalContactProfileNGAvailable,
  isUserProfileNGAvailable,
  role,
}) {
  if (
    isUserProfileNGAvailable &&
    (isDwellerRole({ role }) || isStaffUser({ role }))
  ) {
    return PROFILE_ROUTE_NAMES.userProfileNG({ id })
  }

  if (isExternalContactProfileNGAvailable && isExternalContactUser({ role })) {
    return PROFILE_ROUTE_NAMES.externalContactProfileNG({ id })
  }

  return null
}

function extractProfile(response) {
  const root = response?.data || response

  return root?.results?.objects?.[0]
}

function throwNotFoundPage() {
  throw new Response('Not found page', {
    status: 404,
    statusText: 'Not found page',
  })
}

function redirectToInhabitantsExternal() {
  throw new Response('Redirect to webapp', {
    headers: {
      Location: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}/${WEBAPP_ROUTE_PREFIX}${URL_ALIASES.inhabitants}`,
    },
    status: 302,
    statusText: 'Redirect to webapp',
  })
}

// Decorates the route's own loader so a malformed identifier 404s before any
// request is sent. It must wrap the loader of the very route that spends the
// param: react-router resolves all matched loaders in one `Promise.all`, so a
// guard on a parent route would fire too late.
function getParamGuardLoader({ loader, paramName, regExp }) {
  return loaderProps => {
    if (!regExp.test(loaderProps.params?.[paramName])) {
      throwNotFoundPage()
    }

    return loader ? loader(loaderProps) : null
  }
}

function getSettingsPageWebappPath({ path }) {
  return `${ROUTE_PATH_LIST.settings}/${path}`
}

export function isLegacySubParam(value) {
  return Object.keys(LEGACY_SUBMENU_PARAM_REPLACERS).includes(value)
}

export function isValidSubParam(value) {
  return value && !isLegacySubParam(value)
}

export function isSubArchive(sub) {
  return (
    sub === SUBMENU_PARAM_ARCHIVE_VALUE || sub === SUBMENU_PARAM_TRASH_VALUE
  )
}

export function isSubActive(sub) {
  return sub === SUBMENU_PARAM_ACTIVE_VALUE
}

export function isSubAll(sub) {
  return sub === SUBMENU_ALL_SECTION
}

export function isSubRead(sub) {
  return sub === SUBMENU_PARAM_READ_VALUE
}

export function isSubUnread(sub) {
  return sub === SUBMENU_PARAM_UNREAD_VALUE
}

export function isSubDone(sub) {
  return sub === SUBMENU_DONE_SECTION
}

export function getValidSubParam(value) {
  return isValidSubParam(value) ? value : LEGACY_SUBMENU_PARAM_REPLACERS[value]
}

export function getWebappLinkPrefix(link, withTrailingSlash = false) {
  return `/${WEBAPP_ROUTE_PREFIX}${getPreparedLink(link)}${withTrailingSlash ? '/' : EMPTY_STRING}`
}

export function getAvailableSectionWithRedirectToWebappLoader({
  hasAccess,
  redirectSectionName = EMPTY_STRING,
  request,
}) {
  const url = new URL(request.url)
  const pathname = url.pathname
  const origin = url.origin

  const isAlreadyInWebapp =
    pathname.startsWith(`/${WEBAPP_ROUTE_PREFIX}`) ||
    origin.includes(WEBAPP_ROUTE_PREFIX) ||
    origin.includes('localhost')

  if (isAlreadyInWebapp) {
    return null
  }

  if (!hasAccess) {
    throw new Response('Redirect to webapp', {
      headers: {
        Location: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}/${WEBAPP_ROUTE_PREFIX}/${redirectSectionName}`,
      },
      status: 302,
      statusText: 'Redirect to webapp',
    })
  }

  return null
}

export function getPermissionProtectedRouteLoader({ hasAccess }) {
  if (!hasAccess) {
    throwNotFoundPage()
  }

  return null
}

export function getPermissionGuardLoader({ sectionName, sections }) {
  return loaderProps => {
    const hasAccess = getSectionCanViewPermission(sections, sectionName)

    return getPermissionProtectedRouteLoader({ ...loaderProps, hasAccess })
  }
}

// With the section rolled out the webapp bounces this profile back here, so
// only a switched-off section may redirect to the webapp; non-admin viewers
// (the endpoints are admin-only) get a 404 instead.
export function getExternalContactProfileGuardLoader({
  availableSections,
  isExternalContactProfileNGAvailable,
  sections,
}) {
  return loaderProps => {
    getPermissionGuardLoader({
      sectionName: ROUTE_UI_SECTION_NAME.userProfileDetail,
      sections,
    })(loaderProps)

    if (!availableSections?.[ROUTE_UI_SECTION_NAME.userProfileDetail]) {
      throw new Response('Redirect to webapp', {
        headers: {
          Location: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}${PROFILE_ROUTE_NAMES.externalContacts({ id: loaderProps.params?.inhabitantId })}`,
        },
        status: 302,
        statusText: 'Redirect to webapp',
      })
    }

    return getPermissionProtectedRouteLoader({
      ...loaderProps,
      hasAccess: isExternalContactProfileNGAvailable,
    })
  }
}

// The NG provider profile is gated per viewer (staff, or dweller/landlord
// with the uk exposing provider details) — send everyone else to the webapp
// profile instead.
export function getProviderProfileGuardLoader({
  isProviderProfileNGAvailable,
  sections,
}) {
  return loaderProps => {
    if (!isProviderProfileNGAvailable) {
      throw new Response('Redirect to webapp', {
        headers: {
          Location: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}${PROFILE_ROUTE_NAMES.provider({ id: loaderProps.params?.contractorId })}`,
        },
        status: 302,
        statusText: 'Redirect to webapp',
      })
    }

    return getPermissionGuardLoader({
      sectionName: ROUTE_UI_SECTION_NAME.providerProfileDetail,
      sections,
    })(loaderProps)
  }
}

// Old webapp paths (/chatroom/:uuid, /profile/:id, ...) are kept as redirect
// routes so old bookmarks and email-notification links honor the redesign
// rollout flags. Without a resolved NG pathname the loader falls back to the
// same path in the webapp, keeping any legacy tab segments.
export function getLegacyWebappRedirectLoader({
  getNGPathname = EMPTY_FUNCTION,
} = EMPTY_OBJECT) {
  return async loaderProps => {
    const { pathname, search } = new URL(loaderProps.request.url)
    const ngPathname = await getNGPathname(loaderProps)

    if (!ngPathname) {
      throw new Response('Redirect to webapp', {
        headers: {
          Location: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}/${WEBAPP_ROUTE_PREFIX}${pathname}${search}`,
        },
        status: 302,
        statusText: 'Redirect to webapp',
      })
    }

    return redirect(`${ngPathname}${search}`)
  }
}

export function getChatLegacyRedirectLoader({ availableSections }) {
  return getLegacyWebappRedirectLoader({
    getNGPathname: ({ params }) =>
      availableSections?.[ROUTE_UI_SECTION_NAME.chat]
        ? PROFILE_ROUTE_NAMES.chatProfileNG(params.chatroomUuid)
        : null,
  })
}

export function getBuildingLegacyRedirectLoader({ availableSections }) {
  return getLegacyWebappRedirectLoader({
    getNGPathname: ({ params }) =>
      availableSections?.[ROUTE_UI_SECTION_NAME.buildingDetail]
        ? PROFILE_ROUTE_NAMES.buildingProfileNG({ id: params.buildingId })
        : null,
  })
}

export function getBuildingGroupLegacyRedirectLoader({ availableSections }) {
  return getLegacyWebappRedirectLoader({
    getNGPathname: ({ params }) =>
      availableSections?.[ROUTE_UI_SECTION_NAME.buildingGroupProfileDetail]
        ? PROFILE_ROUTE_NAMES.buildingGroupProfileNG({
            id: params.buildingGroupId,
          })
        : null,
  })
}

// The webapp /profile/:id serves users of any role while the NG profiles are
// split per role, so the target's role has to be fetched before dispatching.
export function getProfileLegacyRedirectLoader({
  isExternalContactProfileNGAvailable,
  isUserProfileNGAvailable,
}) {
  return getLegacyWebappRedirectLoader({
    getNGPathname: async ({ params }) => {
      if (!isUserProfileNGAvailable && !isExternalContactProfileNGAvailable) {
        return null
      }

      const profile = await getUserProfileTinyById({
        id: params.inhabitantId,
        withExternalContacts: isExternalContactProfileNGAvailable,
      })

      return profile
        ? getUserProfileNGPathname({
            id: params.inhabitantId,
            isExternalContactProfileNGAvailable,
            isUserProfileNGAvailable,
            role: profile.group,
          })
        : null
    },
  })
}

export function getUuidParamGuardLoader({ loader, paramName }) {
  return getParamGuardLoader({ loader, paramName, regExp: REG_EXP_UUID })
}

export function getIntegerParamGuardLoader({ loader, paramName }) {
  return getParamGuardLoader({ loader, paramName, regExp: REG_EXP_INTEGER })
}

export function getSectionWebappRedirectGuardLoader({
  availableSections,
  searchParamKey = SEARCH_RESULTS_QUERY_PARAM_KEY,
  sectionName,
  sections,
  webappEntity,
}) {
  return loaderProps => {
    const searchQuery =
      new URL(loaderProps.request.url).searchParams.get(searchParamKey) ||
      EMPTY_STRING

    getAvailableSectionWithRedirectToWebappLoader({
      ...loaderProps,
      hasAccess: !!availableSections?.[sectionName],
      redirectSectionName: `${webappEntity}/search/${encodeURIComponent(searchQuery)}`,
    })

    return getPermissionProtectedRouteLoader({
      hasAccess: getSectionCanViewPermission(sections, sectionName),
    })
  }
}

export function normalizePhoneForSearchProfileRoute(rawPhone) {
  return (rawPhone || EMPTY_STRING).replace(REG_EXP_PHONE_PREFIX, EMPTY_STRING)
}

export function createSearchProfileLoader({
  availableSections,
  isExternalContactProfileNGAvailable,
  isUserProfileNGAvailable,
  normalize,
  param,
}) {
  return async ({ params }) => {
    const hasInhabitantsSection =
      !!availableSections?.[ROUTE_UI_SECTION_NAME.inhabitants]
    const rawParam = params?.[param]

    const value = normalize ? normalize(rawParam) : rawParam

    if (!value || String(value).trim() === EMPTY_STRING) {
      if (hasInhabitantsSection) {
        throw redirect(URL_ALIASES.inhabitants)
      }

      redirectToInhabitantsExternal()
    }

    const response = await getUserProfileTiny({ [param]: value })
    const profile = extractProfile(response)
    const profileId = getUserId(profile)

    if (profileId) {
      const ngPathname = getUserProfileNGPathname({
        id: profileId,
        isExternalContactProfileNGAvailable,
        isUserProfileNGAvailable,
        role: profile.group,
      })

      if (ngPathname) {
        throw redirect(ngPathname)
      }

      if (isExternalContactUser({ role: profile.group })) {
        throw new Response('Redirect to webapp', {
          headers: {
            Location: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}${PROFILE_ROUTE_NAMES.externalContacts({ id: profileId })}`,
          },
          status: 302,
          statusText: 'Redirect to webapp',
        })
      }

      throw new Response('Redirect to webapp', {
        headers: {
          Location: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}/${WEBAPP_ROUTE_PREFIX}/profile/${profileId}`,
        },
        status: 302,
        statusText: 'Redirect to webapp',
      })
    }

    if (hasInhabitantsSection) {
      throw redirect(URL_ALIASES.inhabitants)
    }

    redirectToInhabitantsExternal()
  }
}

export function createServiceRequestProfileLoader({ availableSections }) {
  return ({ params }) => {
    if (!availableSections?.[ROUTE_UI_SECTION_NAME.serviceRequest]) {
      throw new Response('Redirect to webapp', {
        headers: {
          Location: `/${ROUTER_EXTERNAL_REDIRECT_PREFIX}${PROFILE_ROUTE_NAMES.request({ id: params.requestId })}`,
        },
        status: 302,
        statusText: 'Redirect to webapp',
      })
    }

    return redirect(
      PROFILE_ROUTE_NAMES.requestProfileNG({ id: params.requestId })
    )
  }
}

export function getEmailIntegrationSettingsWebappPath({ params, request }) {
  const { search } = new URL(request.url)
  const accountId = params?.[ROUTE_PARAM_EMAIL_ACCOUNT_ID]
  const webappPath = `${ROUTE_PATH_LIST.settings}/${ROUTE_PATH_LIST_SETTINGS.emailIntegrationNew}/${WEBAPP_EMAIL_INTEGRATION_ACCOUNTS_TAB}`

  return accountId ? `${webappPath}/${accountId}` : `${webappPath}/${search}`
}

export function getSettingsPageGuardLoader({
  availableSections,
  getWebappPath = getSettingsPageWebappPath,
  path,
  sectionName,
  sections,
}) {
  return loaderProps => {
    getAvailableSectionWithRedirectToWebappLoader({
      ...loaderProps,
      hasAccess: !!availableSections?.[sectionName],
      redirectSectionName: getWebappPath({ ...loaderProps, path }),
    })

    return getPermissionGuardLoader({ sectionName, sections })(loaderProps)
  }
}

export function createPostProfileLoader({ availableSections, sections }) {
  return loaderProps => {
    getAvailableSectionWithRedirectToWebappLoader({
      ...loaderProps,
      hasAccess: !!availableSections?.[ROUTE_UI_SECTION_NAME.post],
      redirectSectionName: `post/${loaderProps.params.postId}`,
    })

    return getPermissionGuardLoader({
      sectionName: ROUTE_UI_SECTION_NAME.post,
      sections,
    })(loaderProps)
  }
}

export function getBaseDomain(host) {
  return host.split('.').slice(-2).join('.')
}

export function getSubSearchParam(searchParams) {
  return (
    searchParams.get(SUBMENU_PARAM_KEY) ||
    searchParams.get(SUBMENU_PARAM_KEY_OLD)
  )
}

export function getUtfParameter(name) {
  return `utm_content=${name}`
}

export function getUtfCampaignParameter(name = UTM_HOME_PARAMETER) {
  return `utm_campaign=${name}`
}
